import React, { useState } from 'react';
import { useAuthContext } from '../components/AuthProvider';
import { useSubscription } from '../hooks/useSubscription';
import { SubscriptionService, PLANS } from '../services/SubscriptionService';
import PlanCard from '../components/subscription/PlanCard';

const SubscriptionPage = () => {
	const { isAuthenticated } = useAuthContext();
	const {
		subscription,
		isLoading: subscriptionLoading,
		hasActiveSubscription,
		getEffectivePlanName,
		getBillingInterval,
		isCancelingAtPeriodEnd,
		getPeriodEndDate,
		getScheduledDowngrade,
		getExpandedAccessState,
		refreshSubscription
	} = useSubscription();

	// Get current billing interval from subscription
	const currentBillingInterval = getBillingInterval();
	
	// Map backend interval to frontend interval for selector
	const getFrontendInterval = (backendInterval) => {
		const map = {
			month: 'monthly',
			'4months': 'quarterly',
			year: 'yearly'
		};
		return map[backendInterval] || 'monthly';
	};

	const [billingInterval, setBillingInterval] = useState(
		currentBillingInterval ? getFrontendInterval(currentBillingInterval) : 'monthly'
	);
	const [isProcessing, setIsProcessing] = useState(false);
	const [error, setError] = useState(null);
	const [successMessage, setSuccessMessage] = useState(null);

	// Single source of truth for UI + upgrade/downgrade routing (includes trialing)
	const effectivePlanName = getEffectivePlanName();
	const planRank = { essentials: 0, essayist: 1, laureate: 2 };

	const isPaidPlan =
		hasActiveSubscription() &&
		(effectivePlanName === 'essayist' || effectivePlanName === 'laureate');

	const { expired: expandedAccessEndedRaw, endsAt: expandedAccessEndsAtRaw } =
		getExpandedAccessState();
	const expandedAccessEnded = !isPaidPlan && expandedAccessEndedRaw;
	const expandedAccessEndsAt = !isPaidPlan ? expandedAccessEndsAtRaw : null;
	const isOnExpandedAccessTrial = !isPaidPlan && !expandedAccessEnded && !!expandedAccessEndsAt;

	// Update billing interval selector when subscription loads
	React.useEffect(() => {
		if (currentBillingInterval && !subscriptionLoading) {
			setBillingInterval(getFrontendInterval(currentBillingInterval));
		}
	}, [currentBillingInterval, subscriptionLoading]);

	/**
	 * Handle plan selection (subscribe, upgrade, or downgrade)
	 */
	const handlePlanSelect = async (priceId, planName) => {
		try {
			setIsProcessing(true);
			setError(null);
			setSuccessMessage(null);

			// Determine target plan id from display name
			const targetPlanId = Object.keys(PLANS).find(
				(key) => PLANS[key].name.toLowerCase() === planName.toLowerCase()
			);

			// Always use effective paid plan (not a stale Essentials placeholder)
			const currentId = effectivePlanName || 'essentials';

			const isDowngrade =
				targetPlanId &&
				planRank[targetPlanId] !== undefined &&
				planRank[currentId] !== undefined &&
				planRank[targetPlanId] < planRank[currentId];

			const isSamePlanRenewal =
				targetPlanId &&
				targetPlanId === currentId &&
				isPaidPlan;

			const scheduleDowngradeTo = async (targetPriceId, targetDisplayName) => {
				const response = await SubscriptionService.scheduleDowngrade(targetPriceId);
				if (response.status === 200) {
					setSuccessMessage(
						`Your plan will downgrade to ${targetDisplayName} at the end of your current billing period.`
					);
					await refreshSubscription();
					return;
				}
				throw new Error(response.message || 'Failed to schedule downgrade');
			};

			if (isDowngrade) {
				await scheduleDowngradeTo(priceId, planName);
			} else if (isSamePlanRenewal) {
				// Early renew same plan in-place (resets allowances + billing period)
				const renewResult = await SubscriptionService.renewEarly();
				if (renewResult.status === 200) {
					setSuccessMessage(
						renewResult.message ||
							`${planName} renewed. Your document and source allowances have been reset for the new billing period.`
					);
					await refreshSubscription();
				} else if (renewResult.requiresCheckout || renewResult.status === 402) {
					const baseUrl = window.location.origin;
					const successUrl = `${baseUrl}/subscription/success?session_id={CHECKOUT_SESSION_ID}`;
					const cancelUrl = `${baseUrl}/subscription/cancel`;
					const response = await SubscriptionService.createCheckoutSession(
						priceId,
						successUrl,
						cancelUrl,
						{ allowEarlyRenewalCheckout: true }
					);
					if (response.status === 200 && response.url) {
						window.location.href = response.url;
					} else {
						throw new Error(response.message || 'Failed to start renewal checkout');
					}
				} else {
					throw new Error(renewResult.message || 'Failed to renew subscription');
				}
			} else {
				// Upgrades and new subscriptions use Stripe Checkout for immediate change
				const baseUrl = window.location.origin;
				const successUrl = `${baseUrl}/subscription/success?session_id={CHECKOUT_SESSION_ID}`;
				const cancelUrl = `${baseUrl}/subscription/cancel`;

				const response = await SubscriptionService.createCheckoutSession(
					priceId,
					successUrl,
					cancelUrl
				);

				if (response.status === 200 && response.url) {
					window.location.href = response.url;
				} else if (response.code === 'ALREADY_ON_PLAN') {
					await refreshSubscription();
					setSuccessMessage(
						response.message ||
							'Your subscription is already active. Your account has been refreshed.'
					);
				} else if (response.code === 'ALREADY_ON_HIGHER_PLAN') {
					// FE thought we were on Essentials, but Stripe already has a higher plan
					const existing = (response.existingPlan || '').toLowerCase();
					const targetRank = planRank[targetPlanId] ?? -1;
					const existingRank = planRank[existing] ?? -1;
					await refreshSubscription();
					if (targetPlanId && existingRank > targetRank) {
						await scheduleDowngradeTo(priceId, planName);
					} else {
						setSuccessMessage(
							response.message ||
								'Your subscription is already active. Your account has been refreshed.'
						);
					}
				} else {
					throw new Error(response.message || 'Failed to create checkout session');
				}
			}
		} catch (err) {
			console.error('Error creating checkout session:', err);
			const data = err.response?.data;
			if (data?.code === 'ALREADY_ON_PLAN') {
				await refreshSubscription();
				setSuccessMessage(
					data.message ||
						'Your subscription is already active. Your account has been refreshed.'
				);
				setError(null);
			} else if (data?.code === 'ALREADY_ON_HIGHER_PLAN') {
				try {
					const targetPlanId = Object.keys(PLANS).find(
						(key) => PLANS[key].name.toLowerCase() === planName.toLowerCase()
					);
					const existing = (data.existingPlan || '').toLowerCase();
					const targetRank = planRank[targetPlanId] ?? -1;
					const existingRank = planRank[existing] ?? -1;
					await refreshSubscription();
					if (targetPlanId && existingRank > targetRank && priceId) {
						const response = await SubscriptionService.scheduleDowngrade(priceId);
						if (response.status === 200) {
							setSuccessMessage(
								`Your plan will downgrade to ${planName} at the end of your current billing period.`
							);
							await refreshSubscription();
							setError(null);
							return;
						}
					}
					setSuccessMessage(
						data.message ||
							'Your subscription is already active. Your account has been refreshed.'
					);
					setError(null);
				} catch (downgradeErr) {
					setError(
						downgradeErr?.response?.data?.message ||
							downgradeErr.message ||
							data.message ||
							'Failed to schedule downgrade'
					);
				}
			} else {
				setError(
					data?.message ||
						err.message ||
						'Failed to start checkout. Please try again.'
				);
			}
		} finally {
			setIsProcessing(false);
		}
	};

	/**
	 * Open Stripe Customer Portal for subscription management
	 */
	const handleManageSubscription = async () => {
		try {
			setIsProcessing(true);
			setError(null);

			const baseUrl = window.location.origin;
			const returnUrl = `${baseUrl}/subscription`;

			const response = await SubscriptionService.createPortalSession(returnUrl);

			if (response.status === 200 && response.url) {
				// Redirect to Stripe Customer Portal
				window.location.href = response.url;
			} else {
				throw new Error('Failed to create portal session');
			}
		} catch (err) {
			console.error('Error creating portal session:', err);
			const msg = err.response?.data?.message || err.message || '';
			// Friendly message when customer has no Stripe account (e.g. Essentials, never subscribed)
			if (
				msg.toLowerCase().includes('stripe account') ||
				msg.toLowerCase().includes('customer does not have')
			) {
				setError(
					'Subscription management is only available after your first payment. If you’re on Essentials, use the plan cards below to upgrade. If you have an active plan and still see this, please contact support at info@writeformapp.com.'
				);
			} else {
				setError(msg || 'Failed to open subscription management. Please try again.');
			}
		} finally {
			setIsProcessing(false);
		}
	};

	/**
	 * Format date for display
	 */
	const formatDate = (dateString) => {
		if (!dateString) return 'N/A';
		const date = new Date(dateString);
		return date.toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		});
	};

	/**
	 * Format billing interval for display
	 */
	const formatBillingInterval = (interval) => {
		const intervalMap = {
			month: 'Monthly',
			'4months': 'Semester',
			year: 'Annual'
		};
		return intervalMap[interval] || interval;
	};

	// Show loading while checking authentication
	if (!isAuthenticated) {
		return (
			<div className="flex min-h-screen items-center justify-center bg-bgColor">
				<div className="text-center">
					<div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-dark-blue border-t-transparent"></div>
					<p className="text-gray">Loading...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-full bg-bgColor py-8 px-4 sm:px-6 lg:px-8 pb-16">
			<div className="mx-auto max-w-7xl">
				{/* Header */}
				<div className="mb-8 text-center">
					<h1 className="text-main font-bold text-dark-blue leading-39">Subscription Plans</h1>
					<p className="mt-2 text-sub text-gray">
						Choose the plan that best fits your needs
					</p>
					
					{/* Current Plan Badge */}
					{isPaidPlan && subscription && (
						<div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange to-purple text-white rounded-full shadow-lg font-semibold">
							<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
								<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
							</svg>
							<span>
								Active: {PLANS[effectivePlanName]?.name} - {formatBillingInterval(subscription.billingInterval)}
							</span>
						</div>
					)}
					{isOnExpandedAccessTrial && !subscriptionLoading && (
						<div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange to-purple text-white rounded-full shadow-lg font-semibold">
							<span>Essentials — 7-day expanded access</span>
						</div>
					)}
					{expandedAccessEnded && !subscriptionLoading && (
						<div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-dark-blue text-white rounded-full shadow-lg font-semibold">
							<span>Essentials — free plan</span>
						</div>
					)}
				</div>

				{/* Expanded access trial (Essentials / no paid plan) */}
				{isOnExpandedAccessTrial && !subscriptionLoading && (
					<div className="mb-8 rounded-xl bg-lightPurple border border-lightGrey p-6">
						<div className="flex-1">
							<h2 className="text-lg font-semibold text-dark-blue">
								Current Plan: {PLANS[effectivePlanName]?.name || 'Essentials'}
							</h2>
							<p className="mt-1 text-sm text-gray leading-relaxed">
								Your 7-day expanded access ends on{' '}
								{formatDate(expandedAccessEndsAt)}. After that, your
								account will continue on the free Essentials plan with standard usage limits.
								Upgrade anytime for more documents, more sources, and expanded features.
							</p>
						</div>
					</div>
				)}
				{expandedAccessEnded && !subscriptionLoading && (
					<div className="mb-8 rounded-xl bg-lightPurple border border-lightGrey p-6">
						<div className="flex-1">
							<h2 className="text-lg font-semibold text-dark-blue">
								Current Plan: Essentials
							</h2>
							<p className="mt-1 text-sm text-gray leading-relaxed">
								Your 7-day expanded access has ended. You&apos;re on the free Essentials plan
								with standard limits. Upgrade to Essayist or Laureate for more documents,
								sources, and document types.
							</p>
						</div>
					</div>
				)}

				{/* Paid subscription renewal info */}
				{isPaidPlan && (
					<div className="mb-8 rounded-xl bg-lightPurple border border-lightGrey p-6">
						<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
							<div className="flex-1">
								<h2 className="text-lg font-semibold text-dark-blue">
									Current Plan: {PLANS[effectivePlanName]?.name || 'Unknown'}
									{subscription?.billingInterval && (
										<span className="ml-2 text-base font-normal text-gray">
											({formatBillingInterval(subscription.billingInterval)})
										</span>
									)}
								</h2>
								<p className="mt-1 text-sm text-gray">
									{isCancelingAtPeriodEnd()
										? `Your subscription will stop renewing on ${formatDate(
												getPeriodEndDate()
										  )}. You keep full access until this date.`
										: `Renews on ${formatDate(
												getPeriodEndDate()
										  )} unless you cancel before this date. You keep access until the end of each paid term.`}
								</p>
								{getScheduledDowngrade() && (
									<p className="mt-1 text-sm text-gray">
										{`You will switch to ${
											PLANS[getScheduledDowngrade().planName]?.name ||
											getScheduledDowngrade().planName
										} on ${formatDate(getScheduledDowngrade().effectiveDate)}. Until then, you remain on ${
											PLANS[effectivePlanName]?.name
										}.`}
									</p>
								)}
							</div>
						</div>
					</div>
				)}

				{/* Manage subscription is always available when authenticated */}
				<div className="mb-6 flex justify-center">
					<button
						onClick={handleManageSubscription}
						disabled={isProcessing}
						className="h-[52px] rounded-lg bg-gradient-to-r from-orange to-purple px-5 py-2 font-semibold text-white hover:opacity-90 disabled:bg-lightGrey disabled:text-gray disabled:cursor-not-allowed whitespace-nowrap transition-opacity"
					>
						{isProcessing ? 'Loading...' : 'Manage Subscription'}
					</button>
				</div>

				{/* Billing Interval Selector */}
				<div className="mb-6 flex justify-center">
					<div className="inline-flex rounded-xl border border-[#E5E7EB] bg-white p-1">
						{['monthly', 'quarterly', 'yearly'].map((interval) => {
							// Map frontend interval to backend interval format
							const backendIntervalMap = {
								monthly: 'month',
								quarterly: '4months',
								yearly: 'year'
							};
							const backendInterval = backendIntervalMap[interval];
							const isCurrentInterval = subscription?.billingInterval === backendInterval;
							
							return (
								<button
									key={interval}
									onClick={() => setBillingInterval(interval)}
									className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
										billingInterval === interval
											? 'bg-gradient-to-r from-orange to-purple text-white'
											: isCurrentInterval
											? 'bg-lightGreen text-textGreen border border-lightGreen'
											: 'text-dark-blue hover:bg-gray-50'
									}`}
									title={isCurrentInterval ? 'Your current billing interval' : ''}
								>
									{interval === 'quarterly'
										? 'Semester'
										: interval === 'yearly'
										? 'Annual'
										: interval.charAt(0).toUpperCase() + interval.slice(1)}
									{isCurrentInterval && ' ✓'}
								</button>
							);
						})}
					</div>
				</div>

				{/* Non-refundability and grace period note */}
				<div className="mb-6 text-center text-xs text-gray max-w-2xl mx-auto">
					Semester and Annual plans are prepaid and non-refundable  (no exports, no watermark removal, no advanced feature usage). When you cancel, renewals stop but you keep access until the end of your current billing term.
				</div>

				{/* Error Message */}
				{error && (
					<div className="mb-6 rounded-xl bg-lightRed border border-lightRed p-4">
						<p className="text-sm text-textRed">{error}</p>
					</div>
				)}

				{/* Success Message */}
				{successMessage && (
					<div className="mb-6 rounded-xl bg-lightGreen border border-lightGreen p-4">
						<p className="text-sm text-textGreen">{successMessage}</p>
					</div>
				)}

				{/* Loading State */}
				{subscriptionLoading ? (
					<div className="flex justify-center py-12">
						<div className="text-gray">Loading subscription plans...</div>
					</div>
				) : (
					/* Plan Cards */
					<div className="grid gap-6 md:grid-cols-3">
						{Object.values(PLANS).map((plan) => {
							// Normalize plan name comparison (handle case differences)
							const planNameLower = plan.name.toLowerCase();
							// Same effective plan as header / handlePlanSelect (no shadowing)
							const currentPlanLower = (effectivePlanName || 'essentials').toLowerCase();
							const isCurrentPlan = currentPlanLower === planNameLower;
							
							// Check if this is the exact same plan and billing interval
							const backendIntervalMap = {
								monthly: 'month',
								quarterly: '4months',
								yearly: 'year'
							};
							const isExactMatch = isCurrentPlan && (
								// Free Essentials has no Stripe interval — always exact for that card
								effectivePlanName === 'essentials' ||
								subscription?.billingInterval === backendIntervalMap[billingInterval]
							);
							
							// Get price ID for the selected billing interval
							const priceId = plan.priceIds[billingInterval];
							
							return (
								<PlanCard
									key={plan.name}
									plan={plan}
									billingInterval={billingInterval}
									isCurrentPlan={isCurrentPlan}
									isExactMatch={isExactMatch}
									isLoading={isProcessing}
									onSelect={priceId ? handlePlanSelect : undefined}
								/>
							);
						})}
					</div>
				)}

			</div>
		</div>
	);
};

export default SubscriptionPage;

